import React, { useMemo, useState } from "react";
import Image from "next/image";
import styles from "./index.module.scss";
import { useFormik } from "formik";
import * as yup from "yup";
import TitleBlock from "../TitleBlock";
import cn from "classnames";
import useWindowWidth from "hooks/useWindowWidth";
import { SURVEY_URL } from "../../RevenueCalculator/constants";
import SurveyForm from "../../UI/forms/SurveyForm";

const validationSchema = yup.object({
  email: yup.string().email().required(),
  fullName: yup.string().required(),
  company: yup.string().required(),
  position: yup.string().required(),
});

const GetGrowthCases = () => {
  const [submitting, setIsSubmitting] = useState(0);
  const windowWidth = useWindowWidth();

  const onSubmit = (values) => {
    fetch(`${SURVEY_URL}?id=white_paper`, {
      method: "POST",
      body: JSON.stringify({
        data: {
          url: values.website,
          email: values.email,
          full_name: values.fullName,
          position: values.position,
        },
      }),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((r) => {
        if (r.status > 199 && r.status < 300) {
          setIsSubmitting(2);
          const link = document.createElement("a");
          link.href = "/white-paper.pdf";
          link.download = "Apphud White Paper 5 App Growth Cases.pdf";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          setIsSubmitting(0);
        }
      })
      .catch(() => {
        setIsSubmitting(0);
      })
      .finally(() => formik.resetForm());
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      fullName: "",
      company: "",
      position: "",
    },
    validateOnChange: false,
    onSubmit,
    validationSchema,
  });

  const form = useMemo(() => {
    if (submitting === 0) {
      return (
        <SurveyForm
          submitHandler={onSubmit}
          submitting={submitting}
          variant="checklist"
        />
      );
    }
    if (submitting === 2) {
      return (
        <div className={`${styles.root_submit} ${styles.form}`}>
          <Image
            src={
              windowWidth > 992
                ? "/images/icons/subs/confirm.svg"
                : "/images/icons/subs/confirm_mobile.svg"
            }
            width={windowWidth > 992 ? "376" : "288"}
            height={windowWidth > 992 ? "268" : "205"}
            alt=""
            loading="lazy"
          />
          <div
            className={cn(
              "color_dodger-blue",
              "title_md",
              "features",
              "align_center",
              "font_bold"
            )}
          >
            Thank you!
            <br />
            Your white paper has
            <br />
            been downloaded
          </div>
        </div>
      );
    }
  }, [submitting, formik]);

  return (
    <>
      <div className="background_blue">
        <div className={cn("container", styles.container)}>
          <div className={styles.row_1}>
            <TitleBlock
              nobg={true}
              titleWrapper={[styles.marginBottom, styles.noPadding]}
              title={"White paper: \n 5 Growth Cases for Subscription Apps"}
              titleType={[
                "title_2xl",
                "color_white",
                "align_left",
                styles.marginBottom,
              ]}
              subtitle={
                "In the fast-paced app world, staying ahead is key. This document provides 5 growth cases ranging from user acquisition to win-back. Each section is supported by a practical case study designed to give you the tools you need to make a tangible impact on your marketing and product strategy development."
              }
              subtitleType={[
                "text_lg",
                "color_white",
                "align_left",
                "opacity_08",
              ]}
            />
            <div className={styles.instructions}>
              {windowWidth > 992 ? (
                <>
                  <span>By implementing these strategies, you will</span>
                  <span>increase user engagement, boost revenue</span>
                  <span>generation, and foster a loyal customer base.</span>
                </>
              ) : (
                <span>
                  By implementing these strategies, you will increase user
                  engagement, boost revenue generation, and foster a loyal
                  customer base.
                </span>
              )}
            </div>
          </div>

          <div className={styles.row}>{form}</div>
        </div>
      </div>
    </>
  );
};

export default GetGrowthCases;
